import React, { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MessageCircle, MapPin, Send, Loader2 } from "lucide-react";
import { SITE, SERVICES } from "@/lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.message.trim()) {
      toast.error("Bitte Name und Nachricht ausfüllen.");
      return;
    }
    if (!form.email.trim() && !form.phone.trim()) {
      toast.error("Bitte E-Mail oder Telefonnummer angeben.");
      return;
    }
    try {
      setLoading(true);
      await axios.post(`${API}/contact`, form);
      setSubmitted(true);
      toast.success("Vielen Dank! Wir melden uns zeitnah bei Ihnen.");
      setForm({ name: "", email: "", phone: "", service: "", message: "" });
    } catch (err) {
      toast.error("Es ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="kontakt" className="relative bg-white py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="max-w-3xl"
        >
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-100 text-navy text-xs font-bold uppercase tracking-[0.2em]">
            <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: "#4ea83a" }} />
            Kontakt
          </div>
          <h2 className="font-heading mt-5 text-4xl md:text-5xl lg:text-6xl font-black text-navy tracking-tight leading-[1.05]">
            Vereinbaren Sie Ihre <span className="text-shine">kostenlose Besichtigung.</span>
          </h2>
          <p className="mt-5 text-lg text-slate-600 leading-relaxed">
            Wir beraten Sie unverbindlich und erstellen Ihnen ein faires Angebot. Erreichbar persönlich
            per Telefon, WhatsApp oder E-Mail.
          </p>
        </motion.div>

        <div className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* Contact methods */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-5 space-y-4"
          >
            {/* Ansprechpartner Karte */}
            <div
              data-testid="contact-person-card"
              className="flex items-center gap-5 p-6 rounded-3xl ring-1 ring-slate-200"
              style={{ background: "linear-gradient(135deg, #ffffff 0%, #f3f6f9 100%)" }}
            >
              <div className="w-16 h-16 rounded-2xl bg-white ring-1 ring-slate-200 shadow-sm p-2 flex items-center justify-center flex-shrink-0">
                <img src={SITE.logo} alt="Scharpf Logo" className="w-full h-full object-contain" />
              </div>
              <div>
                <div className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500">
                  Ihr Ansprechpartner
                </div>
                <div className="font-heading text-2xl font-extrabold text-navy mt-0.5">
                  {SITE.contactPerson}
                </div>
                <div className="text-sm text-slate-600 mt-0.5">
                  Inhaber · Persönliche Beratung vor Ort
                </div>
              </div>
            </div>
            <a
              href={`tel:${SITE.phoneE164}`}
              data-testid="contact-phone-button"
              className="group flex items-start gap-5 p-6 rounded-3xl bg-surface hover:bg-navy hover:text-white transition-colors ring-1 ring-slate-200"
            >
              <div className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: "rgba(78,168,58,0.15)" }}>
                <Phone className="w-5 h-5" style={{ color: "#3d8a2c" }} />
              </div>
              <div>
                <div className="text-xs font-bold uppercase tracking-[0.18em] opacity-70">Telefon</div>
                <div className="font-heading text-2xl font-extrabold mt-1">{SITE.phoneDisplay}</div>
                <div className="text-sm opacity-70 mt-1">Mo – Sa · 8:00 – 20:00 Uhr</div>
              </div>
            </a>

            <a
              href={`https://wa.me/${SITE.whatsapp}?text=${encodeURIComponent("Hallo, ich interessiere mich für Ihre Leistungen.")}`}
              target="_blank"
              rel="noreferrer"
              data-testid="contact-whatsapp-button"
              className="group flex items-start gap-5 p-6 rounded-3xl bg-surface hover:bg-navy hover:text-white transition-colors ring-1 ring-slate-200"
            >
              <div className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: "rgba(78,168,58,0.15)" }}>
                <MessageCircle className="w-5 h-5" style={{ color: "#3d8a2c" }} />
              </div>
              <div>
                <div className="text-xs font-bold uppercase tracking-[0.18em] opacity-70">WhatsApp</div>
                <div className="font-heading text-2xl font-extrabold mt-1">{SITE.phoneDisplay}</div>
                <div className="text-sm opacity-70 mt-1">Schnelle Antwort</div>
              </div>
            </a>

            <a
              href={`mailto:${SITE.email}`}
              data-testid="contact-email-button"
              className="group flex items-start gap-5 p-6 rounded-3xl bg-surface hover:bg-navy hover:text-white transition-colors ring-1 ring-slate-200"
            >
              <div className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: "rgba(78,168,58,0.15)" }}>
                <Mail className="w-5 h-5" style={{ color: "#3d8a2c" }} />
              </div>
              <div>
                <div className="text-xs font-bold uppercase tracking-[0.18em] opacity-70">E-Mail</div>
                <div className="font-heading text-xl font-extrabold mt-1 break-all">{SITE.email}</div>
                <div className="text-sm opacity-70 mt-1">Antwort innerhalb 24 Stunden</div>
              </div>
            </a>

            <div className="flex items-start gap-5 p-6 rounded-3xl bg-navy text-white" style={{ backgroundColor: "#1e3a5f" }}>
              <div className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0 bg-white/10">
                <MapPin className="w-5 h-5" style={{ color: "#7dd960" }} />
              </div>
              <div className="flex-1">
                <div className="text-xs font-bold uppercase tracking-[0.18em] opacity-80">Adresse</div>
                <div className="font-heading text-xl font-extrabold mt-1 leading-tight">
                  {SITE.addressStreet}
                </div>
                <div className="font-heading text-base font-bold opacity-90">{SITE.addressCity}</div>
                <div className="mt-4 pt-4 border-t border-white/15">
                  <div className="text-xs font-bold uppercase tracking-[0.18em] opacity-80 mb-2">
                    Einsatzgebiet
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {SITE.serviceAreas.map((a) => (
                      <span
                        key={a}
                        className="px-2.5 py-1 rounded-full text-xs font-semibold bg-white/10 border border-white/15"
                      >
                        {a}
                      </span>
                    ))}
                  </div>
                  <div className="mt-2 text-xs opacity-75">{SITE.serviceAreaNote}</div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Form */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="lg:col-span-7"
          >
            <form
              onSubmit={submit}
              data-testid="contact-form"
              className="rounded-[2rem] bg-white shadow-xl ring-1 ring-slate-200 p-7 md:p-10"
            >
              {submitted ? (
                <div className="text-center py-10" data-testid="contact-success">
                  <div className="mx-auto w-16 h-16 rounded-full flex items-center justify-center" style={{ backgroundColor: "rgba(78,168,58,0.15)" }}>
                    <Send className="w-7 h-7" style={{ color: "#3d8a2c" }} />
                  </div>
                  <h3 className="font-heading mt-5 text-2xl font-extrabold text-navy">Vielen Dank!</h3>
                  <p className="mt-2 text-slate-600">
                    Ihre Anfrage ist bei uns eingegangen. Wir melden uns schnellstmöglich bei Ihnen zurück.
                  </p>
                  <button
                    type="button"
                    onClick={() => setSubmitted(false)}
                    data-testid="contact-send-another"
                    className="mt-6 px-5 py-2.5 rounded-full bg-navy text-white text-sm font-semibold hover:bg-navy-deep transition-colors"
                    style={{ backgroundColor: "#1e3a5f" }}
                  >
                    Weitere Anfrage senden
                  </button>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-semibold text-navy">Name *</label>
                      <input
                        data-testid="contact-name-input"
                        value={form.name}
                        onChange={update("name")}
                        type="text"
                        required
                        placeholder="Ihr vollständiger Name"
                        className="mt-2 w-full px-4 py-3 rounded-2xl bg-slate-50 border border-slate-200 focus:outline-none focus:ring-2 focus:ring-green-accent focus:border-transparent text-slate-900 placeholder:text-slate-400"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-semibold text-navy">Telefon</label>
                      <input
                        data-testid="contact-phone-input"
                        value={form.phone}
                        onChange={update("phone")}
                        type="tel"
                        placeholder="z. B. 0172 1234567"
                        className="mt-2 w-full px-4 py-3 rounded-2xl bg-slate-50 border border-slate-200 focus:outline-none focus:ring-2 focus:ring-green-accent focus:border-transparent text-slate-900 placeholder:text-slate-400"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-semibold text-navy">E-Mail</label>
                      <input
                        data-testid="contact-email-input"
                        value={form.email}
                        onChange={update("email")}
                        type="email"
                        placeholder="ihre@email.de"
                        className="mt-2 w-full px-4 py-3 rounded-2xl bg-slate-50 border border-slate-200 focus:outline-none focus:ring-2 focus:ring-green-accent focus:border-transparent text-slate-900 placeholder:text-slate-400"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-semibold text-navy">Leistung</label>
                      <select
                        data-testid="contact-service-select"
                        value={form.service}
                        onChange={update("service")}
                        className="mt-2 w-full px-4 py-3 rounded-2xl bg-slate-50 border border-slate-200 focus:outline-none focus:ring-2 focus:ring-green-accent text-slate-900"
                      >
                        <option value="">Bitte auswählen</option>
                        {SERVICES.map((s) => (
                          <option key={s.id} value={s.short || s.title}>{s.short || s.title}</option>
                        ))}
                        <option value="Sonstiges">Sonstiges</option>
                      </select>
                    </div>
                  </div>

                  <div className="mt-4">
                    <label className="text-sm font-semibold text-navy">Ihre Nachricht *</label>
                    <textarea
                      data-testid="contact-message-input"
                      value={form.message}
                      onChange={update("message")}
                      required
                      rows={5}
                      placeholder="Beschreiben Sie kurz Ihr Anliegen (Objektgröße, Adresse, Wunschtermin …)"
                      className="mt-2 w-full px-4 py-3 rounded-2xl bg-slate-50 border border-slate-200 focus:outline-none focus:ring-2 focus:ring-green-accent focus:border-transparent text-slate-900 placeholder:text-slate-400 resize-none"
                    />
                  </div>

                  <p className="mt-4 text-xs text-slate-500">
                    Mit dem Absenden stimmen Sie zu, dass wir Sie zur Bearbeitung Ihrer Anfrage kontaktieren dürfen.
                  </p>

                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="contact-submit-button"
                    className="mt-6 group w-full md:w-auto inline-flex items-center justify-center gap-3 px-8 py-4 rounded-full text-white text-base md:text-lg font-bold shadow-lg hover:-translate-y-0.5 hover:shadow-xl transition-all disabled:opacity-70 disabled:cursor-not-allowed"
                    style={{ backgroundColor: "#4ea83a" }}
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" /> Wird gesendet …
                      </>
                    ) : (
                      <>
                        Kostenlose Besichtigung anfragen
                        <Send className="w-5 h-5 group-hover:translate-x-0.5 transition-transform" />
                      </>
                    )}
                  </button>
                </>
              )}
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
